// Footer business groups (footerNav.js, "My Activity" + "Elakay & Roles"): the business destinations of the EFFECTIVE role
// (RoleSwitchDropdown-aware), grouped by branch. Built only from the existing
// navigation sources -- Seller My Shop links (menuUser / User Settings), the
// Filter drawer's Dropshipping In config, the Dropshipping Out platform
// registry, the Provider workspace list and the User Settings role management
// cards -- so every route exists in Routes.js and every Animation Settings gate
// (Sell Center status, dropshippingVisibility, dropshippingOut) is honoured:
// a disabled feature stays visible but inert ("Soon"), exactly like the Sell
// Center hub. Permission first: a status never grants a capability.
//
// Seller-only groups need the Seller branch, Provider-only groups the Provider
// branch; Associate..Family (and a Family switched to one of them) get both.
import { AiFillShop } from "react-icons/ai";
import { FaSellcast, FaFirstOrder, FaTruck, FaRoute, FaBoxes, FaGlobe, FaUsers, FaCrown, FaHandshake, FaUserCog, FaHistory } from "react-icons/fa";
import { MdDeleteForever, MdPublic, MdCheckBox, MdIndeterminateCheckBox, MdMessage, MdTrendingUp, MdAddBusiness } from "react-icons/md";
import { GiSellCard, GiFamilyTree } from "react-icons/gi";
import { elakayAutoText } from "../pageInside/dataElakay";
import {
  getCommercialCapabilities,
  getCurrentElakayRole,
  canViewDropshippingSellerCenter,
  canViewDropshippingPlatform,
  Assoociate,
  Investment,
  Adminstrator,
  Family,
} from "../pageInside/Rolle";
import { getSellCenterFeatureStatus } from "../../utils/sellCenterStatus";
import { buildDrawerNav, statusLabel } from "../../components/SIdeDrawer/drawerNav";
import { buildProviderWorkspace } from "../dropshippingInterne/providerWorkspace";
import { getDropshippingOutPlatforms, isDropshippingServiceActive } from "../pages/dropshippingOutPlatforms";
import { SELLER_HISTORY_ROUTES } from "../dropshippingInterne/history/sellerHistoryRoutes";

export const MY_BUSINESS_GROUP_KEYS = [
  "seller",
  "dropshippingInSeller",
  "dropshippingOut",
  "provider",
  "dropshippingInProvider",
  "roleManagement",
];

const GROUP_TITLES = {
  seller: ["h_mbiz_group_seller", "Seller"],
  dropshippingInSeller: ["h_mbiz_group_di_seller", "Dropshipping In — Seller"],
  dropshippingOut: ["h_mbiz_group_ds_out", "External Dropshipping"],
  provider: ["h_mbiz_group_provider", "Provider"],
  dropshippingInProvider: ["h_mbiz_group_di_provider", "Dropshipping In — Provider"],
  roleManagement: ["h_mbiz_group_roles", "Role Management"],
};

// Provider workspace entries (providerWorkspace.js) that belong to the
// Provider side of internal dropshipping; every other entry is Provider core.
const PROVIDER_DROPSHIPPING_KEYS = ["dropshipping", "connectedSellers", "paPlans"];

const drawerText = (infoSource, key, fallback) =>
  infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.drawerNavigation?.[key] || fallback;

const section = (infoSource, name) => {
  try {
    return infoSource?.laguangeElakay?.(name, infoSource.languageAPI) || {};
  } catch (error) {
    return {};
  }
};

// Provider workspace labels, in the translated `drawerNavigation` namespace
// when it has the same destination, else a My Business key.
const PROVIDER_LABELS = {
  dashboard: (infoSource) => drawerText(infoSource, "providerCenter", "Provider Center"),
  myShop: (infoSource) => drawerText(infoSource, "myShop", "My Shop"),
  builder: () => elakayAutoText("h_mbiz_provider_builder", "Provider Page Builder"),
  products: (infoSource) => drawerText(infoSource, "myProducts", "My Products"),
  addProduct: (infoSource) => drawerText(infoSource, "addProduct", "Add Product"),
  inventory: (infoSource) => drawerText(infoSource, "inventory", "Inventory"),
  orders: (infoSource) => drawerText(infoSource, "orders", "Orders"),
  connectedSellers: (infoSource) => drawerText(infoSource, "connectedSellers", "My Connected Sellers"),
  dropshipping: () => elakayAutoText("h_mbiz_provider_dropshipping", "Dropshipping Management"),
  wholesale: () => elakayAutoText("h_mbiz_wholesale", "Wholesale Management"),
  paPlans: (infoSource) => drawerText(infoSource, "paPlans", "PA Plans"),
  settings: (infoSource) => drawerText(infoSource, "settings", "Settings"),
};

const providerItem = (infoSource, item) => ({
  key: `provider-${item.key}`,
  route: item.route,
  icon: item.icon,
  label: (PROVIDER_LABELS[item.key] || (() => item.label))(infoSource),
  status: item.status || "active",
});

// Tools both branches legitimately share (delivery, route scan, email
// marketing). Listed once: in the Seller group when the role has it, else in
// the Provider group.
// Email Marketing is listed only when the caller passes the server-resolved
// `canAccessEmailMarketing` (options.emailMarketing); omitted options keep
// the legacy listing for callers that don't resolve it.
const sharedTools = (menu, options = {}) => [
  { key: "delivery", route: "/delivery", icon: FaTruck, label: menu?.delivery || elakayAutoText("h_delivery_center", "Delivery Center"), status: "active" },
  { key: "postman", route: "/postman?scan=1", icon: FaRoute, label: menu?.postman || elakayAutoText("h_postman_route", "Postman / Delivery Route"), status: "active" },
  ...(options.emailMarketing === false ? [] : [
    { key: "emailMarketing", route: "/elakay/email-marketing-dashboard", icon: MdMessage, label: menu?.emailMarketingDashboard || elakayAutoText("h_emk_menu_dashboard_link", "Email Marketing"), status: "active" },
  ]),
];

const sellerGroup = (infoSource, menu, title) => [
  { key: "myShop", route: "/myshop", icon: AiFillShop, label: menu?.myShop || elakayAutoText("h_mbiz_seller_myshop", "My Shop"), status: "active" },
  { key: "sell", route: "/myshop?sel=true", icon: FaSellcast, label: menu?.sell || elakayAutoText("h_mbiz_seller_sell", "Sell"), status: "active" },
  { key: "myShopPub", route: "/myshop?mb=true&BPUB=false", icon: MdPublic, label: title?.myShopPub || elakayAutoText("h_mbiz_seller_pub", "My Shop Pub"), status: "active" },
  { key: "customerOrders", route: "/myshop?odr=true", icon: FaFirstOrder, label: menu?.buyerCustomer || elakayAutoText("h_mbiz_seller_orders", "Customer Orders"), status: "active" },
  { key: "inDeal", route: "/myshop?td=true", icon: MdCheckBox, label: menu?.yourProInDeal || elakayAutoText("h_mbiz_seller_in_deal", "Products in Deal"), status: "active" },
  { key: "outDeal", route: "/myshop?fd=true", icon: MdIndeterminateCheckBox, label: menu?.yourProOutDeal || elakayAutoText("h_mbiz_seller_out_deal", "Products out of Deal"), status: "active" },
  { key: "trash", route: "/myshop?trash=1", icon: MdDeleteForever, label: menu?.trash || elakayAutoText("h_nav_trash", "Trash"), status: "active" },
];

const externalDropshippingGroup = (infoSource, role) => {
  const settings = infoSource?.allSettings?.dropshippingOut;
  const sellCenterStatus = getSellCenterFeatureStatus(infoSource?.allSettings, "dropshippingOut", role);
  const serviceStatus = sellCenterStatus !== "active"
    ? sellCenterStatus
    : isDropshippingServiceActive(settings) ? "active" : "soon";
  const platforms = getDropshippingOutPlatforms(settings)
    .filter((platform) => canViewDropshippingPlatform(infoSource, platform.id))
    .map((platform) => ({
      key: `platform-${platform.id}`,
      route: platform.route,
      icon: FaGlobe,
      label: platform.name,
      status: serviceStatus !== "active" ? serviceStatus : platform.active ? "active" : "soon",
    }));
  return [
    { key: "platforms", route: "/dropshipping/platforms", icon: FaBoxes, label: elakayAutoText("h_mbiz_ds_platforms", "All Platforms"), status: serviceStatus },
    ...platforms,
  ];
};

// Mirrors the User Settings "Add a role" cards: each tier adds the roles it may grant.
const roleManagementGroup = (menu) => {
  const items = [];
  if (Assoociate()) {
    items.push(
      { key: "viewUsers", route: "/admin_associat", icon: FaUsers, label: menu?.viewUser || elakayAutoText("h_mbiz_role_view_users", "View Users") },
      { key: "addRoyalty", route: "/admin_associat?page=usersAdd&usel=1", icon: FaCrown, label: menu?.addUserRoyalty || elakayAutoText("h_mbiz_role_add_royalty", "Add User Royalty") },
      { key: "addSeller", route: "/admin_associat?page=usersAdd&usel=2", icon: MdTrendingUp, label: menu?.addSeller || elakayAutoText("h_mbiz_role_add_seller", "Add Seller") },
      { key: "addProvider", route: "/admin_associat?page=usersAdd&usel=3", icon: GiSellCard, label: menu?.addProducer || elakayAutoText("h_mbiz_role_add_provider", "Add Provider") },
    );
  }
  if (Investment()) items.push({ key: "addAssociate", route: "/admin_associat?page=usersAdd&usel=4", icon: FaHandshake, label: menu?.addAssociate || elakayAutoText("h_mbiz_role_add_associate", "Add Associate") });
  if (Adminstrator()) items.push({ key: "addInvestor", route: "/admin_associat?page=usersAdd&usel=5", icon: MdAddBusiness, label: menu?.addInvestment || elakayAutoText("h_mbiz_role_add_investor", "Add Investor") });
  if (Family()) {
    items.push(
      { key: "addAdministrator", route: "/admin_associat?page=usersAdd&usel=6", icon: FaUserCog, label: menu?.addAdministrator || elakayAutoText("h_mbiz_role_add_admin", "Add Administrator") },
      { key: "addFamily", route: "/admin_associat?page=usersAdd&usel=7", icon: GiFamilyTree, label: menu?.addFamilly || elakayAutoText("h_mbiz_role_add_family", "Add Developer") },
    );
  }
  return items.map((item) => ({ ...item, status: "active" }));
};

// [{ key, title, items: [{ key, route, icon, label, status, statusText }] }], only non-empty groups,
// in MY_BUSINESS_GROUP_KEYS order. A route is listed once (first group wins).
export const buildMyBusinessNav = (infoSource, options = {}) => {
  const role = getCurrentElakayRole(infoSource);
  const caps = getCommercialCapabilities(infoSource);
  const menu = section(infoSource, "menuSertting");
  const title = section(infoSource, "title");
  const hasSeller = caps.canAccessSellerMyShop;
  const hasProvider = caps.canAccessProviderCenter;
  const groups = {};

  if (hasSeller) groups.seller = [...sellerGroup(infoSource, menu, title), ...sharedTools(menu, options)];

  const drawer = buildDrawerNav(infoSource);
  const drawerDropshippingIn = drawer.find((group) => group.key === "dropshippingIn");
  if (drawerDropshippingIn) {
    groups.dropshippingInSeller = drawerDropshippingIn.items.map((item) => ({
      key: `di-${item.key}`, route: item.route, icon: item.icon, label: item.label, status: item.status,
    }));
  }

  if (caps.canAccessSellerCenter && canViewDropshippingSellerCenter(infoSource)) {
    groups.dropshippingOut = externalDropshippingGroup(infoSource, role);
  }

  if (hasProvider) {
    const workspace = buildProviderWorkspace(infoSource);
    groups.provider = [
      ...workspace.filter((item) => !PROVIDER_DROPSHIPPING_KEYS.includes(item.key)).map((item) => providerItem(infoSource, item)),
      ...(hasSeller ? [] : sharedTools(menu, options)),
    ];
    const providerDropshipping = workspace
      .filter((item) => PROVIDER_DROPSHIPPING_KEYS.includes(item.key))
      .map((item) => providerItem(infoSource, item));
    if (caps.canManageWholesale) {
      providerDropshipping.push({ key: "provider-subscribers", route: "/provider/subscriptions", icon: FaUsers, label: drawerText(infoSource, "subscribers", "Subscribers"), status: "active" });
    }
    groups.dropshippingInProvider = providerDropshipping;
  }

  groups.roleManagement = roleManagementGroup(menu);

  const seen = new Set();
  return MY_BUSINESS_GROUP_KEYS
    .map((key) => ({
      key,
      title: elakayAutoText(...GROUP_TITLES[key]),
      items: (groups[key] || [])
        .filter((item) => {
          if (seen.has(item.route)) return false;
          seen.add(item.route);
          return true;
        })
        .map((item) => ({ ...item, statusText: item.status === "active" ? null : statusLabel(infoSource, item.status) })),
    }))
    .filter((group) => group.items.length);
};

// Seller-side groups of the User Settings menu (menuUser.jsx) and page
// (userSerting.js), next to their Seller My Shop links -- the same lists as
// the footer's My Business nav, so routes, permissions and Animation Settings
// statuses stay identical everywhere. The Seller Sourcing History pages
// (/seller/provider-history/*, reached from the shop header) are added to
// Dropshipping In with the same status as the Providers link.
export const SELLER_SETTINGS_GROUP_KEYS = ["dropshippingInSeller", "dropshippingOut"];

export const buildSellerSettingsNav = (infoSource) =>
  buildMyBusinessNav(infoSource, { emailMarketing: false })
    .filter((group) => SELLER_SETTINGS_GROUP_KEYS.includes(group.key))
    .map((group) => {
      if (group.key !== "dropshippingInSeller") return group;
      const providers = group.items.find((item) => item.key === "di-providers");
      if (!providers) return group;
      const history = [
        { key: "di-historyProviders", route: SELLER_HISTORY_ROUTES.providers, label: elakayAutoText("h_sph_providers_title", "Recently Viewed Providers") },
        { key: "di-historyProducts", route: SELLER_HISTORY_ROUTES.products, label: elakayAutoText("h_sph_products_title", "Recently Viewed Provider Products") },
      ].map((item) => ({ ...item, icon: FaHistory, status: providers.status, statusText: providers.statusText }));
      return { ...group, items: [...group.items, ...history] };
    });

